/**
 * Listing detail — the single-listing view for SocialPulse Local.
 *
 * Layout:
 *   ┌───────────────────────────────────────────────────────────────┐
 *   │  Breadcrumb                                                    │
 *   │  Media gallery (uses first media, falls back to hero color)    │
 *   │  Title  ·  Type badge  ·  Price / compensation                 │
 *   │  Description                                                   │
 *   │  Location + coarse map                                         │
 *   │  Contact / Save / Share buttons                                │
 *   │  Attributes                                                    │
 *   └───────────────────────────────────────────────────────────────┘
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowLeft, MapPin, MessageCircle, Bookmark, Share2, Tag, Clock,
} from "lucide-react";
import { localApi, COMPENSATION_LABEL, formatMoney, typeByKey } from "@/lib/localApi";
import ShareMenu from "@/components/ShareMenu";
import { toast } from "sonner";


function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}


export default function ListingDetail() {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setLoading(true);
    localApi.get(`/listings/${id}`)
      .then(({ data }) => setListing(data))
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
    // Fire-and-forget view counter.
    localApi.post(`/listings/${id}/view`).catch(() => {});
  }, [id]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center opacity-50">Loading…</div>;
  }
  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-3">
        <p className="opacity-60">This listing has expired or been removed.</p>
        <Link to="/local" className="wofy-btn-ghost">← Back to SocialPulse Local</Link>
      </div>
    );
  }
  if (!listing) return null;

  const t = typeByKey(listing.listing_type);
  const primaryMedia = listing.media?.[0];
  const price = listing.pricing?.[0];
  const loc = listing.location || {};
  const shareUrl = `${window.location.origin}/local/listings/${listing.id}`;

  return (
    <div
      data-testid="listing-detail"
      className="min-h-screen"
      style={{ background: "var(--wofy-bg)" }}
    >
      <Helmet>
        <title>{`${listing.title} · SocialPulse Local`}</title>
        <meta name="description" content={listing.description?.slice(0, 160)} />
        <meta property="og:title" content={listing.title} />
        <meta property="og:description" content={listing.description?.slice(0, 160)} />
        {primaryMedia && <meta property="og:image" content={primaryMedia} />}
      </Helmet>

      <div className="max-w-[1000px] mx-auto px-5 lg:px-8 py-8">
        {/* Breadcrumb */}
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] mb-6 opacity-60">
          <Link to="/local" className="hover:opacity-100">SocialPulse Local</Link>
          <span className="mx-2">/</span>
          <span>{listing.listing_type}</span>
        </div>

        {/* Media gallery */}
        <div
          data-testid="listing-media"
          className="rounded-md overflow-hidden mb-8 relative"
          style={{
            aspectRatio: "16/9",
            background: primaryMedia
              ? `url(${primaryMedia}) center/cover no-repeat`
              : "linear-gradient(135deg, rgba(14,124,85,0.15), rgba(31,111,235,0.12))",
          }}
        >
          {!primaryMedia && (
            <div className="absolute inset-0 flex items-center justify-center opacity-70">
              <Tag className="w-10 h-10" style={{ color: "#0E7C55" }} />
            </div>
          )}
        </div>

        {/* Title row */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span
                data-testid="listing-type-badge"
                className="text-[11px] font-mono uppercase tracking-[0.16em] px-2 py-1 rounded"
                style={{ background: "rgba(14,124,85,0.08)", color: "#0E7C55" }}
              >
                {t.label}
              </span>
              {listing.risk_level && listing.risk_level !== "low" && (
                <span className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded bg-amber-100 text-amber-800">
                  {listing.risk_level} risk
                </span>
              )}
            </div>
            <h1
              data-testid="listing-title"
              className="font-display text-3xl md:text-4xl leading-[1.1] tracking-tight"
              style={{ color: "var(--wofy-text)" }}
            >
              {listing.title}
            </h1>
            <div className="mt-2 flex items-center gap-4 text-sm opacity-70">
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {[loc.city, loc.region, loc.country_code].filter(Boolean).join(", ")}
              </span>
              <span className="inline-flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {timeAgo(listing.created_at)}
              </span>
              <span>·  {listing.view_count || 0} views</span>
            </div>
          </div>

          {/* Price */}
          <div className="text-right">
            {price?.model === "free" ? (
              <span className="font-display text-3xl" style={{ color: "#0E7C55" }}>
                Free
              </span>
            ) : price?.amount_minor ? (
              <>
                <div className="font-display text-3xl">
                  {formatMoney(price.amount_minor, price.currency)}
                </div>
                <div className="text-xs opacity-60 uppercase tracking-wider">
                  {COMPENSATION_LABEL[price.model] || price.model}
                </div>
              </>
            ) : price ? (
              <div className="text-lg opacity-80">
                {COMPENSATION_LABEL[price.model] || price.model}
              </div>
            ) : null}
          </div>
        </div>

        {/* Description */}
        {listing.description && (
          <div
            data-testid="listing-description"
            className="mt-8 whitespace-pre-line text-[16px] leading-[1.65]"
            style={{ color: "var(--wofy-text)" }}
          >
            {listing.description}
          </div>
        )}

        {/* Attributes */}
        {listing.attributes && Object.keys(listing.attributes).length > 0 && (
          <dl className="mt-8 grid sm:grid-cols-2 gap-3 border-t pt-6"
              style={{ borderColor: "var(--wofy-border)" }}>
            {Object.entries(listing.attributes).map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm">
                <dt className="opacity-60 uppercase tracking-wider text-[11px]">{k}</dt>
                <dd className="font-mono">{String(v)}</dd>
              </div>
            ))}
          </dl>
        )}

        {/* Tags */}
        {listing.tags?.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-2">
            {listing.tags.map((t) => (
              <Link
                key={t}
                to={`/local?q=${encodeURIComponent(t)}`}
                className="text-[12px] rounded-full px-3 py-1 border"
                style={{ borderColor: "var(--wofy-border)" }}
              >
                #{t}
              </Link>
            ))}
          </div>
        )}

        {/* Coarse map — points to a generic city map on OpenStreetMap */}
        {loc.city && (
          <div
            data-testid="listing-map-link"
            className="mt-8 rounded-md border p-4 flex items-center gap-3"
            style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
          >
            <MapPin className="w-5 h-5" style={{ color: "#0E7C55" }} />
            <div className="flex-1">
              <div className="font-medium text-sm">
                {loc.city}{loc.region ? `, ${loc.region}` : ""}
              </div>
              <div className="text-xs opacity-70">
                Exact address is revealed only after both sides agree in chat.
              </div>
            </div>
            <a
              href={`https://www.openstreetmap.org/search?query=${encodeURIComponent(
                [loc.city, loc.region, loc.country_code].filter(Boolean).join(", "),
              )}`}
              target="_blank" rel="noreferrer"
              className="text-sm underline opacity-70 hover:opacity-100"
            >
              Open in map
            </a>
          </div>
        )}

        {/* Action row */}
        <div className="mt-10 flex flex-wrap items-center gap-2 border-t pt-6"
             style={{ borderColor: "var(--wofy-border)" }}>
          <button
            data-testid="listing-contact"
            onClick={() => toast.info("Messaging launches when Phase 1.3 wraps.")}
            className="wofy-btn-primary inline-flex items-center gap-2"
          >
            <MessageCircle className="w-4 h-4" /> Contact
          </button>
          <button
            data-testid="listing-save"
            onClick={() => { setSaved((s) => !s); toast.success(saved ? "Removed from saved" : "Saved"); }}
            className="wofy-btn-ghost inline-flex items-center gap-2"
          >
            <Bookmark className={`w-4 h-4 ${saved ? "fill-current" : ""}`} />
            {saved ? "Saved" : "Save"}
          </button>
          <ShareMenu
            title={listing.title}
            url={shareUrl}
            trigger={
              <button
                data-testid="listing-share"
                className="wofy-btn-ghost inline-flex items-center gap-2"
              >
                <Share2 className="w-4 h-4" /> Share
              </button>
            }
          />
        </div>

        {/* Back */}
        <Link
          to="/local"
          className="mt-10 inline-flex items-center gap-1.5 text-sm opacity-70 hover:opacity-100 underline"
        >
          <ArrowLeft className="w-4 h-4" /> Back to SocialPulse Local
        </Link>
      </div>
    </div>
  );
}
